// @ts-nocheck
import React, { useState } from 'react';
import { Gavel, Moon, Package, Sun, Truck } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { megaMenuColumns, marketplaceBrand } from '../../config/marketplaceContent';
import { resolveLandingViewMode } from '../../containers/LandingPage/landingViewMode';
import { isCommsOnly } from '../../config/commsMode';
import { railPersonaUrl, isExternalHref } from '../../config/railNavigation';
import { useTheme } from '../../context/ThemeContext';
import { useActivePersonaMaybe } from '../../context/ActivePersonaContext';

import AppsLauncher from '../AppsLauncher/AppsLauncher';
import ConnectivityTicker from './ConnectivityTicker';
import MegaMenuSessionControls from './MegaMenuSessionControls';
import css from './Phase5MegaMenu.module.css';

const iconMap = {
  merchant: Package,
  runner: Truck,
  arbitration: Gavel,
};

const ThemeToggle = () => {
  const { isDark, toggleTheme } = useTheme();
  return (
    <button
      type="button"
      className={css.themeToggle}
      onClick={toggleTheme}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      data-testid="theme-toggle"
    >
      {isDark ? <Sun size={16} /> : <Moon size={16} />}
    </button>
  );
};

const Phase5MegaMenu: React.FC<{ onLogout?: () => void }> = ({ onLogout }) => {
  const { isDark } = useTheme();
  const { pathname } = useLocation();
  const viewMode = resolveLandingViewMode(pathname);
  const personaContext = useActivePersonaMaybe();
  const [open, setOpen] = useState(false);

  const showMerchantNav = personaContext ? personaContext.showMerchantNav : true;
  const showRunnerNav = personaContext ? personaContext.showRunnerNav : true;

  const filteredColumns = megaMenuColumns.filter(column => {
    if (column.id === 'merchant-suite' && !showMerchantNav) {
      return false;
    }
    if (column.id === 'runner-cockpit' && !showRunnerNav) {
      return false;
    }
    return true;
  });

  const navLinkClass = (mode: string | null, href?: string) => {
    const isActive =
      mode === viewMode ||
      (href && pathname === href) ||
      (mode === 'home' && pathname === '/');
    return isActive ? `${css.navLink} ${css.navLinkActive}` : css.navLink;
  };

  const commsOnly = isCommsOnly();
  const runnerHref = commsOnly ? railPersonaUrl('runner') : '/runner';
  const merchantHref = commsOnly ? railPersonaUrl('merchant') : '/merchant';

  const NavHref = ({ href, className, children, ...rest }) =>
    isExternalHref(href) ? (
      <a href={href} className={className} rel="noopener noreferrer" {...rest}>
        {children}
      </a>
    ) : (
      <Link to={href} className={className} {...rest}>
        {children}
      </Link>
    );

  return (
    <div
      className={isDark ? css.shell : css.shellLight}
      onMouseLeave={() => setOpen(false)}
    >
      <div className={css.bar}>
        <Link to="/" className={css.brand}>
          <span className={css.brandMark}>BX</span>
          <span className={css.brandName}>{marketplaceBrand.name}</span>
        </Link>

        <nav className={css.nav} aria-label="Phase 5 operations">
          <Link to="/" className={navLinkClass('home')}>
            Home
          </Link>
          <Link to="/marketplace" className={navLinkClass('marketplace')}>
            Marketplace
          </Link>
          {showRunnerNav ? (
            <NavHref href={runnerHref} className={navLinkClass('runner')}>
              Runner
            </NavHref>
          ) : null}
          <button
            type="button"
            className={css.navLink}
            aria-expanded={open}
            aria-haspopup="true"
            onMouseEnter={() => setOpen(true)}
            onFocus={() => setOpen(true)}
            onClick={() => setOpen(prev => !prev)}
            data-testid="phase5-mega-menu-trigger"
          >
            Operations
          </button>
          {showMerchantNav ? (
            <NavHref href={merchantHref} className={navLinkClass(null, '/merchant')}>
              Merchant
            </NavHref>
          ) : null}
          <Link to="/admin" className={navLinkClass(null, '/admin')}>
            Admin
          </Link>
        </nav>

        <div className={css.actions}>
          <AppsLauncher />
          <Link to="/activity" className={css.activityNavLink} data-testid="activity-nav-link">
            Activity
          </Link>
          <ConnectivityTicker />
          <MegaMenuSessionControls onLogout={onLogout} isDark={isDark} />
          <ThemeToggle />
        </div>
      </div>

      {open ? (
        <div className={css.panel} role="region" aria-label="Operations mega menu" data-testid="phase5-mega-menu">
          <div className={css.grid}>
            {filteredColumns.map(column => {
              const Icon = iconMap[column.icon];
              return (
                <Link
                  key={column.id}
                  to={column.href}
                  className={css.column}
                  onClick={() => setOpen(false)}
                >
                  <div className={css.columnHeader}>
                    <span className={css.columnIcon}>
                      <Icon size={18} />
                    </span>
                    <div>
                      <h3 className={css.columnTitle}>{column.title}</h3>
                      <p className={css.columnSubtitle}>{column.subtitle}</p>
                    </div>
                  </div>
                  <ul className={css.bulletList}>
                    {column.bullets.map(bullet => (
                      <li key={bullet}>{bullet}</li>
                    ))}
                  </ul>
                </Link>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Phase5MegaMenu;
